import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star, Eye, Plus, Heart } from 'lucide-react';
import { API_BASE_URL } from '../../api';
import { getSelectedVariant, getVariantImages, getDisplayColors, isInStock } from '../../utils/productHelpers';

const renderStars = (rating) => (
  Array.from({ length: 5 }, (_, i) => (
    <Star key={i} className={`w-3.5 h-3.5 ${i < Math.floor(rating) ? 'text-brass-deep fill-current' : 'text-line'}`} />
  ))
);

/**
 * A single catalog product tile. Owns its own image-carousel index and hover
 * state (no reason for these to live in the parent's global state).
 */
const ProductCard = ({ productGroup, isJustAdded, onQuickView, onAddToCart, size = 'default' }) => {
  const [imageIndex, setImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);

  const defaultVariant = getSelectedVariant(productGroup);
  const colors = getDisplayColors(productGroup);
  // The color the card is currently showing — picking a swatch updates this,
  // the image, and what "Add" actually puts in the cart, without opening the
  // full quick-view modal.
  const activeColor = selectedColor || colors[0];
  const matchingVariant = activeColor
    ? (productGroup.variants || []).find((v) => v.color === activeColor.color || v.colorCode === activeColor.colorCode)
    : null;
  const variant = matchingVariant
    ? { ...defaultVariant, ...matchingVariant, image: activeColor.imageUrl || matchingVariant.image || defaultVariant.image }
    : (activeColor?.imageUrl ? { ...defaultVariant, image: activeColor.imageUrl } : defaultVariant);
  const images = getVariantImages(variant, API_BASE_URL);
  const hasMultipleImages = images.length > 1;

  const handleSelectColor = (e, color) => {
    e.stopPropagation();
    setSelectedColor(color);
    setImageIndex(0);
  };

  const handleAddToCart = () => {
    onAddToCart(activeColor && colors.length > 1
      ? { ...productGroup, selectedColor: activeColor.color, selectedColorCode: activeColor.colorCode, selectedImageId: activeColor.imageId, colorName: activeColor.colorName || activeColor.color }
      : productGroup);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setImageIndex((i) => (i + 1) % images.length);
  };
  const prevImage = (e) => {
    e.stopPropagation();
    setImageIndex((i) => (i - 1 + images.length) % images.length);
  };

  const isCompact = size === 'compact';

  return (
    <div
      className="group relative flex flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setImageIndex(0); }}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-surface rounded-sm mb-3">
        <img
          src={images[imageIndex]}
          alt={productGroup.baseName || productGroup.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
          onError={(e) => { e.target.src = '/images/placeholder.jpg'; }}
        />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          {productGroup.isFeatured && (
            <span className="dpc-label bg-ink text-paper px-2.5 py-1 rounded-full text-[10px]">Featured</span>
          )}
          {variant.badge && !productGroup.isFeatured && (
            <span className="dpc-label bg-surface/95 text-ink px-2.5 py-1 rounded-full text-[10px] shadow-sm">{variant.badge}</span>
          )}
          {variant.sale && (
            <span className="dpc-label bg-accent text-paper px-2.5 py-1 rounded-full text-[10px]">Sale</span>
          )}
        </div>

        {!isInStock(variant) && (
          <div className="absolute inset-0 bg-ink/55 flex items-center justify-center">
            <span className="dpc-label text-paper">Out of stock</span>
          </div>
        )}

        {isJustAdded && (
          <div className="absolute inset-0 bg-ink/70 flex items-center justify-center animate-dpc-fade-up">
            <span className="dpc-label text-paper">Added to bag</span>
          </div>
        )}

        {/* Carousel controls */}
        {hasMultipleImages && isHovered && (
          <>
            <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-surface/90 flex items-center justify-center text-ink hover:bg-surface" aria-label="Previous image">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-surface/90 flex items-center justify-center text-ink hover:bg-surface" aria-label="Next image">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, i) => (
                <span key={i} className={`w-1 h-1 rounded-full ${i === imageIndex ? 'bg-surface' : 'bg-surface/40'}`} />
              ))}
            </div>
          </>
        )}

        {/* Hover actions */}
        <div className={`absolute bottom-0 left-0 right-0 p-2.5 flex gap-2 transition-all duration-300 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
          <button
            onClick={() => onQuickView(productGroup)}
            className="flex-1 flex items-center justify-center gap-1.5 bg-surface/95 backdrop-blur-sm text-ink dpc-label text-[10px] py-2.5 rounded-full hover:bg-surface transition-colors"
          >
            <Eye className="w-3.5 h-3.5" /> View
          </button>
          <button
            onClick={handleAddToCart}
            disabled={!isInStock(variant)}
            className="flex-1 flex items-center justify-center gap-1.5 bg-ink text-paper dpc-label text-[10px] py-2.5 rounded-full hover:bg-accent transition-colors disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      </div>

      <div className="text-left">
        <button onClick={() => onQuickView(productGroup)} className="text-left w-full">
          <p className="text-[11px] text-ink-faint dpc-label mb-1">{productGroup.brand}</p>
          <h3 className={`font-display text-ink mb-1.5 leading-snug ${isCompact ? 'text-base' : 'text-lg'}`}>
            {productGroup.baseName || productGroup.name}
          </h3>
          {!isCompact && (
            <div className="flex items-center gap-1 mb-1.5">{renderStars(variant.rating || 0)}</div>
          )}
          <div className="flex items-baseline gap-2 mb-1.5">
            <span className="text-ink font-medium">KSh {Number(variant.price || 0).toLocaleString()}</span>
            {variant.originalPrice > variant.price && (
              <span className="text-ink-faint text-sm line-through">KSh {Number(variant.originalPrice).toLocaleString()}</span>
            )}
          </div>
        </button>
        {colors.length > 1 && (
          <div className="flex items-center gap-1.5">
            {colors.slice(0, 4).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={(e) => handleSelectColor(e, c)}
                className={`w-4 h-4 rounded-full border-2 transition-colors ${activeColor?.id === c.id ? 'border-ink' : 'border-line'}`}
                style={{ background: c.colorCode }}
                aria-label={c.colorName}
                aria-pressed={activeColor?.id === c.id}
                title={c.colorName}
              />
            ))}
            {colors.length > 4 && <span className="text-[11px] text-ink-faint">+{colors.length - 4}</span>}
          </div>
        )}
        {productGroup.tags && productGroup.tags.length > 0 && !isCompact && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {productGroup.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-[10px] text-ink-soft bg-paper border border-line px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const WishlistButton = ({ active, onClick }) => (
  <button onClick={onClick} className="p-2 rounded-full hover:bg-paper transition-colors" aria-label="Toggle wishlist">
    <Heart className={`w-5 h-5 ${active ? 'fill-accent text-accent' : 'text-ink-faint'}`} />
  </button>
);

export default ProductCard;
